<script setup>
import { computed } from 'vue'

const props = defineProps({
  basePlanPrice: { type: Number, required: true },
  silverPlanPrice: { type: Number, required: true },
  goldPlanPrice: { type: Number, required: true },
  basePlanOffered: { type: Boolean, default: true },
  silverPlanOffered: { type: Boolean, default: true },
  goldPlanOffered: { type: Boolean, default: true },
})

const totalRevenue = computed(() => {
  // Simple estimation: 10 students per enabled plan
  let revenue = 0

  if (props.basePlanOffered) {
    revenue += props.basePlanPrice * 10
  }
  if (props.silverPlanOffered) {
    revenue += props.silverPlanPrice * 10
  }
  if (props.goldPlanOffered) {
    revenue += props.goldPlanPrice * 10
  }

  return Math.round(revenue)
})
</script>

<template>
  <section id="pricing-summary" class="mb-8">
    <div class="bg-white rounded-2xl p-5 md;p-8 border border-gray-200">
      <h3 class="text-xl font-bold text-gray-900 mb-6">Pricing Summary & Revenue Projection</h3>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
        <!-- Base Plan -->
        <div :class="[
          'rounded-xl p-6 transition-all',
          basePlanOffered ? 'bg-blue-50' : 'bg-gray-50 opacity-50'
        ]">
          <div class="flex items-center justify-between mb-2">
            <span :class="['text-sm font-medium', basePlanOffered ? 'text-gray-600' : 'text-gray-400']">Content Library</span>
            <span :class="['material-symbols-outlined', basePlanOffered ? 'text-blue-600' : 'text-gray-400']">play_circle</span>
          </div>
          <p :class="['text-3xl font-bold', basePlanOffered ? 'text-gray-900' : 'text-gray-400']">
            £{{ basePlanOffered ? basePlanPrice : 0 }}
          </p>
          <p :class="['text-xs mt-1', basePlanOffered ? 'text-gray-500' : 'text-gray-400']">
            {{ basePlanOffered ? 'weekly subscription' : 'not offered' }}
          </p>
        </div>

        <!-- Silver Plan -->
        <div :class="[
          'rounded-xl p-6 transition-all',
          silverPlanOffered ? 'bg-purple-50' : 'bg-gray-50 opacity-50'
        ]">
          <div class="flex items-center justify-between mb-2">
            <span :class="['text-sm font-medium', silverPlanOffered ? 'text-gray-600' : 'text-gray-400']">Group Tuition</span>
            <span :class="['material-symbols-outlined', silverPlanOffered ? 'text-purple-600' : 'text-gray-400']">groups</span>
          </div>
          <p :class="['text-3xl font-bold', silverPlanOffered ? 'text-gray-900' : 'text-gray-400']">
            £{{ silverPlanOffered ? silverPlanPrice : 0 }}
          </p>
          <p :class="['text-xs mt-1', silverPlanOffered ? 'text-gray-500' : 'text-gray-400']">
            {{ silverPlanOffered ? 'weekly subscription' : 'not offered' }}
          </p>
        </div>

        <!-- Gold Plan -->
        <div :class="[
          'rounded-xl p-6 transition-all',
          goldPlanOffered ? 'bg-yellow-50' : 'bg-gray-50 opacity-50'
        ]">
          <div class="flex items-center justify-between mb-2">
            <span :class="['text-sm font-medium', goldPlanOffered ? 'text-gray-600' : 'text-gray-400']">1:1 Tuition</span>
            <span :class="['material-symbols-outlined', goldPlanOffered ? 'text-yellow-600' : 'text-gray-400']">workspace_premium</span>
          </div>
          <p :class="['text-3xl font-bold', goldPlanOffered ? 'text-gray-900' : 'text-gray-400']">
            £{{ goldPlanOffered ? goldPlanPrice : 0 }}
          </p>
          <p :class="['text-xs mt-1', goldPlanOffered ? 'text-gray-500' : 'text-gray-400']">
            {{ goldPlanOffered ? 'weekly subscription' : 'not offered' }}
          </p>
        </div>

        <!-- Total Revenue -->
        <div class="bg-green-50 rounded-xl p-6">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-600">Est. Weekly</span>
            <span class="material-symbols-outlined text-green-600">trending_up</span>
          </div>
          <p class="text-3xl font-bold text-gray-900">£{{ totalRevenue }}</p>
          <p class="text-xs text-gray-500 mt-1">with 10 students</p>
        </div>
      </div>
    </div>
  </section>
</template>
